import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignQuoteTemplateSectionModel, CampaignQuoteTemplateSectionRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['campaignQuoteTemplateSections']

type CampaignQuoteTemplateSectionFilters = {
  campaign_quote_template_id?: number,
  string?: string,
}

type UseCampaignQuoteTemplateSectionsOptions = {
  filters?: CampaignQuoteTemplateSectionFilters,
  performHttpRequests?: boolean,
  requestOptions?: CampaignQuoteTemplateSectionRequestOptions,
}

function useCampaignQuoteTemplateSections(options: UseCampaignQuoteTemplateSectionsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    campaign_quote_template_id: filterCampaignQuoteTemplateId,
    string: filterString,
  } = filters

  const {
    updatedEntities: {
      campaignQuoteTemplateSections: campaignQuoteTemplateSectionsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaignQuoteTemplateSections } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignQuoteTemplateSections = useMemo(() => {
    const filtered = Object.values(campaignQuoteTemplateSections).filter((
      campaignQuoteTemplateSection: CampaignQuoteTemplateSectionModel,
    ) => {
      const { campaign_quote_template_id, id, title } = campaignQuoteTemplateSection

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchCampaignQuoteTemplateId = matchFilterNumber(campaign_quote_template_id, filterCampaignQuoteTemplateId)

      return matchCampaignQuoteTemplateId && matchString
    })

    return sortArrayBy(filtered, 'asc', 'sort')
  }, [campaignQuoteTemplateSectionsUpdatedAt, JSON.stringify(filters)])

  const filteredCampaignQuoteTemplateSectionIds = filteredCampaignQuoteTemplateSections.map(s => s.id)
  const filteredCampaignQuoteTemplateSectionsCount = filteredCampaignQuoteTemplateSections.length
  const hasFilteredCampaignQuoteTemplateSections = !!filteredCampaignQuoteTemplateSectionsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredCampaignQuoteTemplateSectionsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCampaignQuoteTemplateSections } = useReduxAction(
    'campaignQuoteTemplateSections',
    'loadCampaignQuoteTemplateSections',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  const lastSectionIndex = filteredCampaignQuoteTemplateSections.length - 1

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaignQuoteTemplateSectionIds,
    filteredCampaignQuoteTemplateSections,
    filteredCampaignQuoteTemplateSectionsCount,
    hasFilteredCampaignQuoteTemplateSections,
    lastSectionIndex,
    loading: loadingCampaignQuoteTemplateSections,
  }
}

export default useCampaignQuoteTemplateSections
